// src/hooks/smsReplyHelpers.js
//
// Pure logic for the unified-replies feature (features/unified-replies/).
// Everything in this file is dependency-free on purpose: the Node test
// script tools/test-unified-replies-helpers.cjs imports this module
// directly, so nothing here may touch Firebase, React, or Vite aliases.
//
// The store (smsReplyStore.js) is the only other consumer.

/* -------------------------------------------------------------------- */
/* Role normalization                                                    */
/* -------------------------------------------------------------------- */

// MIRROR of src/constants/roles.js::normalizeTeamRole. That module pulls
// in the Firebase SDK at import time, which the Node test runner can't
// resolve, so the tiny normalizer is mirrored here — same pattern as
// constants/roles.js itself, which mirrors functions/core/roles.js.
// Any new role value must land in all three files.
const STAFF_OWNER_VALUES = ['owner', 'head_coach'];
const STAFF_VALUES = ['staff', 'coach', 'manager', 'assistant', 'team_manager'];

export function normalizeStaffRole(raw) {
  const r = String(raw || '').toLowerCase().trim();
  if (!r) return null;
  if (STAFF_OWNER_VALUES.includes(r)) return 'owner';
  if (STAFF_VALUES.includes(r)) return 'staff';
  return null;
}

/* -------------------------------------------------------------------- */
/* Timestamps                                                            */
/* -------------------------------------------------------------------- */

// Same shape-tolerant conversion useChats.js uses.
export function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts === 'number') return ts;
  if (ts._seconds != null) return ts._seconds * 1000;
  return 0;
}

/* -------------------------------------------------------------------- */
/* Staff team selection (arch A-2)                                       */
/* -------------------------------------------------------------------- */

export const MAX_SMS_TEAMS = 30;

/**
 * Pick the teams whose inboundSms collections we listen to.
 *
 * @param {Array<{teamId, role, name?, joinedAtMs?}>} mirrorEntries docs
 *        from users/{uid}/teams
 * @param {Iterable<string>} discoveredIds teamIds found by the one-time
 *        legacy membership discovery (mirror-absent teams count as staff,
 *        matching useMyTeams; non-staff among them get permission-denied
 *        and are dropped by the store)
 * @param {number} cap
 * @returns {{ teams: Array<{teamId, name}>, dropped: number }}
 */
export function selectStaffTeams(mirrorEntries, discoveredIds = [], cap = MAX_SMS_TEAMS) {
  const byId = new Map();

  for (const e of mirrorEntries || []) {
    if (!e?.teamId) continue;
    if (normalizeStaffRole(e.role) === null) continue;
    byId.set(e.teamId, {
      teamId: e.teamId,
      name: e.name || null,
      joinedAtMs: toMillis(e.joinedAtMs ?? e.joinedAt) || 0,
    });
  }
  for (const id of discoveredIds || []) {
    if (!id || byId.has(id)) continue;
    byId.set(id, { teamId: id, name: null, joinedAtMs: 0 });
  }

  // Deterministic: newest membership first, teamId as tiebreak.
  const sorted = Array.from(byId.values()).sort((a, b) =>
    (b.joinedAtMs - a.joinedAtMs) || (a.teamId < b.teamId ? -1 : a.teamId > b.teamId ? 1 : 0)
  );

  const teams = sorted.slice(0, cap).map(({ teamId, name }) => ({ teamId, name }));
  return { teams, dropped: Math.max(0, sorted.length - cap) };
}

/* -------------------------------------------------------------------- */
/* Unread reduction (security boundary, sec review S2)                   */
/* -------------------------------------------------------------------- */

/**
 * Reduce a team's unread snapshot to the only two values the feature is
 * allowed to keep. Callers must pass receivedAt values ONLY — never doc
 * data — so PII cannot survive past this point.
 *
 * @param {number[]} receivedAtMillisList
 * @returns {{count: number, newestAtMs: number}}
 */
export function reduceUnread(receivedAtMillisList) {
  const list = receivedAtMillisList || [];
  let newestAtMs = 0;
  for (const ms of list) {
    const v = typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
    if (v > newestAtMs) newestAtMs = v;
  }
  return { count: list.length, newestAtMs };
}

/**
 * Build the store's published snapshot (sec review S2: this is the PII
 * boundary — the output contains exactly teamId, name, count, newestAtMs
 * per team and nothing else). Teams without a resolved name are skipped
 * so a row can never render broken (BA edge case, QA T-11); skipping
 * also keeps the badge-units == visible-rows invariant (BA D-2).
 *
 * @param {Map<string, {count, newestAtMs}>} perTeamMap
 * @param {Map<string, string>} namesById
 * @returns {{units: number, perTeam: Array<{teamId, name, count, newestAtMs}>}}
 */
export function buildPublished(perTeamMap, namesById) {
  const perTeam = [];
  for (const [teamId, agg] of perTeamMap.entries()) {
    if (!agg || agg.count <= 0) continue;
    const name = namesById.get(teamId);
    if (!name) continue;
    perTeam.push({
      teamId,
      name,
      count: agg.count,
      newestAtMs: agg.newestAtMs,
    });
  }
  perTeam.sort((a, b) =>
    (b.newestAtMs - a.newestAtMs) || (a.teamId < b.teamId ? -1 : 1)
  );
  return { units: perTeam.length, perTeam };
}

/* -------------------------------------------------------------------- */
/* Settle gate (arch A-4)                                                */
/* -------------------------------------------------------------------- */

/**
 * Tracks which initial team listeners have reported (snapshot OR error).
 * The store holds its first publish until settled so the badge renders
 * once instead of pulsing 0→1→2→3 while snapshots trickle in.
 */
export function makeSettleTracker(teamIds) {
  const pending = new Set(teamIds || []);
  return {
    markFired(teamId) { pending.delete(teamId); },
    get settled() { return pending.size === 0; },
  };
}

/* -------------------------------------------------------------------- */
/* Inbox merge (arch: one memoized sort, chats not mutated)              */
/* -------------------------------------------------------------------- */

/**
 * Interleave chat docs and SMS reply descriptors into one list sorted by
 * recency (BA D-4). Returns wrapper descriptors; the chat objects are
 * passed through by reference, untouched.
 *
 * @param {Array<object>} chats decorated chats from useChats
 * @param {Array<{teamId, name, count, newestAtMs}>} smsPerTeam
 * @returns {Array<{kind: 'chat'|'sms', ts: number, chat?, sms?}>}
 */
export function mergeInboxRows(chats, smsPerTeam) {
  const rows = [];
  for (const chat of chats || []) {
    rows.push({
      kind: 'chat',
      ts: toMillis(chat.lastMessageAt) || toMillis(chat.createdAt),
      chat,
    });
  }
  for (const sms of smsPerTeam || []) {
    rows.push({ kind: 'sms', ts: sms.newestAtMs || 0, sms });
  }
  // Stable in modern JS engines, so equal timestamps keep insertion order.
  rows.sort((a, b) => b.ts - a.ts);
  return rows;
}

/** Combined badge/chip number (BA D-2): unread chats + teams with unread SMS. */
export function combinedUnreadCount(unreadChatCount, smsUnits) {
  return (unreadChatCount || 0) + (smsUnits || 0);
}
